import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Tag, Row, Col,
  Statistic, Modal, Form, Input, Select, Tabs, DatePicker,
  InputNumber, Drawer, Popconfirm, message,
} from 'antd'
import { ToolOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const API = ''

function fmtINR(v: number) {
  return `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

const ASSET_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'success', DISPOSED: 'error', SCRAPPED: 'warning', IDLE: 'default',
}

export default function FixedAssetsPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>()
  const [depPeriod, setDepPeriod] = useState<dayjs.Dayjs>(dayjs())
  const [viewDepLines, setViewDepLines] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [addAssetDrawer, setAddAssetDrawer] = useState(false)
  const [assetForm] = Form.useForm()

  const { data: assetsData, isLoading: aLoading } = useQuery({
    queryKey: ['fixed-assets', plantId, statusFilter, categoryFilter],
    queryFn: () =>
      apiClient.get(`${API}/accounting/fixed-assets`, {
        params: { plant_id: plantId, status: statusFilter || undefined, category_id: categoryFilter || undefined, limit: 500 },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: depRunsData, isLoading: dLoading } = useQuery({
    queryKey: ['dep-runs', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/depreciation/runs`, { params: { plant_id: plantId, limit: 50 } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['asset-categories', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/asset-categories`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: statsData } = useQuery({
    queryKey: ['asset-stats', plantId],
    queryFn: () =>
      apiClient.get(`${API}/accounting/fixed-assets/stats`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const runDepMut = useMutation({
    mutationFn: () =>
      apiClient.post(`${API}/accounting/depreciation/run`, {
        plant_id: plantId,
        period_year: depPeriod.year(),
        period_month: depPeriod.month() + 1,
      }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dep-runs'] })
      qc.invalidateQueries({ queryKey: ['fixed-assets'] })
      qc.invalidateQueries({ queryKey: ['asset-stats'] })
      message.success('Depreciation run completed')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const createAssetMut = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`${API}/accounting/fixed-assets`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fixed-assets'] })
      qc.invalidateQueries({ queryKey: ['asset-stats'] })
      message.success('Asset added')
      setAddAssetDrawer(false)
      assetForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const assets = Array.isArray(assetsData?.entries ?? assetsData) ? (assetsData?.entries ?? assetsData) : []
  const depRuns = Array.isArray(depRunsData?.entries ?? depRunsData) ? (depRunsData?.entries ?? depRunsData) : []
  const categories = Array.isArray(categoriesData) ? categoriesData : []
  const stats = statsData ?? {}

  const assetColumns = [
    { title: 'Asset Code', dataIndex: 'asset_code', key: 'asset_code', width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v}</span> },
    { title: 'Asset Name', dataIndex: 'asset_name', key: 'asset_name', width: 180, ellipsis: true },
    { title: 'Category', dataIndex: 'category_name', key: 'category_name', width: 120 },
    { title: 'Location', dataIndex: 'location', key: 'location', width: 110 },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 110 },
    { title: 'Capitalised', dataIndex: 'capitalization_date', key: 'capitalization_date', width: 100,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Original Cost', dataIndex: 'original_cost', key: 'original_cost', width: 130, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Accum. Dep', dataIndex: 'accumulated_dep', key: 'accumulated_dep', width: 120, align: 'right' as const,
      render: (v: number) => <span style={{ color: '#ff4d4f' }}>{fmtINR(v)}</span> },
    { title: 'Book Value (WDV)', key: 'book_value', width: 130, align: 'right' as const,
      render: (_: unknown, r: any) => {
        const bv = Number(r.original_cost ?? 0) - Number(r.accumulated_dep ?? 0)
        return <span style={{ color: '#52c41a', fontWeight: 600 }}>{fmtINR(bv)}</span>
      } },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={ASSET_STATUS_COLORS[v] ?? 'default'}>{v}</Tag> },
  ]

  const depRunColumns = [
    { title: 'Period', key: 'period', width: 100,
      render: (_: unknown, r: any) => `${r.period_month}/${r.period_year}` },
    { title: 'Run Date', dataIndex: 'run_date', key: 'run_date', width: 110,
      render: (v: string) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
    { title: 'Total Depreciation', dataIndex: 'total_depreciation', key: 'total_depreciation', width: 160, align: 'right' as const,
      render: (v: number) => <b>{fmtINR(v)}</b> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 100,
      render: (v: string) => <Tag color={v === 'COMPLETED' ? 'success' : 'processing'}>{v}</Tag> },
    { title: 'Action', key: 'action', width: 120,
      render: (_: unknown, r: any) => (
        <Button size="small" onClick={() => setViewDepLines(r)}>View Lines</Button>
      ) },
  ]

  const depLineColumns = [
    { title: 'Asset', dataIndex: 'asset_name', key: 'asset_name', ellipsis: true },
    { title: 'Opening WDV', dataIndex: 'opening_wdv', key: 'opening_wdv', width: 130, align: 'right' as const,
      render: (v: number) => fmtINR(v) },
    { title: 'Dep for Period', dataIndex: 'dep_for_period', key: 'dep_for_period', width: 130, align: 'right' as const,
      render: (v: number) => <span style={{ color: '#ff4d4f' }}>{fmtINR(v)}</span> },
    { title: 'Closing WDV', dataIndex: 'closing_wdv', key: 'closing_wdv', width: 130, align: 'right' as const,
      render: (v: number) => <span style={{ color: '#52c41a', fontWeight: 600 }}>{fmtINR(v)}</span> },
  ]

  const tabItems = [
    {
      key: 'register',
      label: 'Asset Register',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <Select value={plantId} onChange={setPlantId} style={{ width: 180 }}
                options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
            </Col>
            <Col>
              <Select allowClear placeholder="Status" style={{ width: 130 }} value={statusFilter} onChange={setStatusFilter}
                options={Object.keys(ASSET_STATUS_COLORS).map(k => ({ value: k, label: k }))} />
            </Col>
            <Col>
              <Select allowClear placeholder="Category" style={{ width: 160 }} value={categoryFilter} onChange={setCategoryFilter}
                options={categories.map((c: any) => ({ value: c.id, label: c.category_name }))} />
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { assetForm.resetFields(); setAddAssetDrawer(true) }}>
                Add Asset
              </Button>
            </Col>
          </Row>
          <Table dataSource={assets} columns={assetColumns} rowKey="id" loading={aLoading}
            size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1200 }} />
        </>
      ),
    },
    {
      key: 'depreciation',
      label: 'Depreciation',
      children: (
        <>
          <Row gutter={12} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <DatePicker picker="month" value={depPeriod} onChange={v => v && setDepPeriod(v)} />
            </Col>
            <Col>
              <Popconfirm
                title="Run Depreciation"
                description={`Run depreciation for ${depPeriod?.format('MMM YYYY')}? This will post depreciation entries for all active assets.`}
                onConfirm={() => runDepMut.mutate()}
                okText="Run"
              >
                <Button type="primary" loading={runDepMut.isPending}>Run Depreciation</Button>
              </Popconfirm>
            </Col>
          </Row>
          <Table dataSource={depRuns} columns={depRunColumns} rowKey="id" loading={dLoading}
            size="small" pagination={{ pageSize: 20 }} />
        </>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ToolOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Fixed Assets Register
      </Title>
      <Text type="secondary">Asset capitalization, depreciation tracking and disposal</Text>

      {/* Stats */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        {[
          { title: 'Total Assets', value: stats.total_assets ?? 0, color: '#1677ff' },
          { title: 'Gross Block', value: fmtINR(stats.gross_block ?? 0), color: '#722ed1' },
          { title: 'Accumulated Dep', value: fmtINR(stats.accumulated_dep ?? 0), color: '#ff4d4f' },
          { title: 'Net Block (WDV)', value: fmtINR(stats.net_block ?? 0), color: '#52c41a' },
        ].map((s, i) => (
          <Col span={6} key={i}>
            <Card size="small">
              <Statistic title={s.title} value={s.value} valueStyle={{ color: s.color, fontSize: 18 }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Tabs items={tabItems} />
      </Card>

      {/* Dep Lines Modal */}
      <Modal
        title={`Depreciation Lines — ${viewDepLines?.period_month}/${viewDepLines?.period_year}`}
        open={!!viewDepLines}
        onCancel={() => setViewDepLines(null)}
        footer={<Button onClick={() => setViewDepLines(null)}>Close</Button>}
        width={700}
      >
        <Table
          dataSource={viewDepLines?.lines ?? []}
          columns={depLineColumns}
          rowKey={(r, i) => String(i)}
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* Add Asset Drawer */}
      <Drawer
        title="Add Fixed Asset"
        open={addAssetDrawer}
        onClose={() => { setAddAssetDrawer(false); assetForm.resetFields() }}
        width={500}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => { setAddAssetDrawer(false); assetForm.resetFields() }}>Cancel</Button>
            <Button type="primary" loading={createAssetMut.isPending}
              onClick={() =>
                assetForm.validateFields().then(vals =>
                  createAssetMut.mutate({
                    ...vals,
                    plant_id: plantId,
                    capitalization_date: vals.capitalization_date?.format('YYYY-MM-DD'),
                  })
                )
              }
            >Save</Button>
          </Space>
        }
      >
        <Form form={assetForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="asset_code" label="Asset Code" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="physical_tag_no" label="Physical Tag No">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="asset_name" label="Asset Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category_id" label="Category" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label"
              options={categories.map((c: any) => ({ value: c.id, label: c.category_name }))} />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="department" label="Department">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="capitalization_date" label="Capitalization Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="original_cost" label="Original Cost (₹)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} addonBefore="₹" />
          </Form.Item>
          <Form.Item name="invoice_no" label="Supplier Invoice No">
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
